/**
 * DemoGuide Component - Subtle floating suggestion for demo mode users
 * Shows next suggested action as a small chip, expandable to see more options
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  Typography,
  IconButton,
  Fade,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  LinearProgress
} from '@mui/material';
import {
  Inventory2 as BoxIcon,
  CameraAlt as CameraIcon,
  PhotoLibrary as MultiPhotoIcon,
  Search as SearchIcon,
  Explore as ExploreIcon,
  LocalOffer as TagIcon,
  SmartToy as AiIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  ArrowForward as ArrowIcon,
  MoreHoriz as MoreIcon,
  Celebration as CelebrationIcon,
  Refresh as RefreshIcon,
  PersonAdd as SignUpIcon
} from '@mui/icons-material';
import { useAppNavigation } from '../hooks/useAppNavigation';
import demoService from '../services/demoService';

const FEATURE_CONFIG = {
  create_box: { icon: BoxIcon, route: 'CREATE_BOX', label: 'Create a box' },
  upload_image: { icon: CameraIcon, route: 'ADD_ITEM', label: 'Upload a photo' },
  multi_item: { icon: MultiPhotoIcon, route: 'ADD_ITEM', label: 'Try multi-item photo' },
  search: { icon: SearchIcon, route: 'FIND', label: 'Search your items' },
  explorer: { icon: ExploreIcon, route: 'LOCATIONS', label: 'Explore locations' },
  tags: { icon: TagIcon, route: 'TAGS', label: 'Browse by tags' },
  ai_chat: { icon: AiIcon, route: 'CHAT', label: 'Ask AI assistant' }
};

const DemoGuide = () => {
  const navigate = useAppNavigation();
  const [demoInfo, setDemoInfo] = useState(null);
  const [progress, setProgress] = useState({ completed: 0, total: 7, percentage: 0 });
  const [dismissed, setDismissed] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    loadDemoInfo();
    
    // Listen for feature completion events
    const handleFeatureCompleted = () => {
      loadDemoInfo();
    };
    window.addEventListener('demoFeatureCompleted', handleFeatureCompleted);
    
    // Refresh demo info periodically as backup
    const interval = setInterval(loadDemoInfo, 10000);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('demoFeatureCompleted', handleFeatureCompleted);
    };
  }, []);

  const loadDemoInfo = () => {
    const info = demoService.getDemoInfo();
    if (info) {
      setDemoInfo(info);
      setProgress(demoService.getDemoProgress());
    }
  };

  const handleFeatureClick = (featureId) => {
    const config = FEATURE_CONFIG[featureId];
    console.log('[DemoGuide] Feature clicked:', featureId, 'config:', config);
    if (config?.route) {
      const navFn = navigate.to[config.route];
      console.log('[DemoGuide] Navigation function:', config.route, 'exists:', !!navFn);
      if (navFn) {
        navFn();
      }
    }
    setMenuAnchor(null);
  };

  const handleReset = async () => {
    setMenuAnchor(null);
    if (!window.confirm('Reset demo? This will restore the original sample data.')) {
      return;
    }
    setResetting(true);
    try {
      await demoService.resetDemoAccount();
      window.location.reload();
    } catch (error) {
      console.error('Failed to reset demo:', error);
      alert('Failed to reset demo. Please try again.');
    } finally {
      setResetting(false);
    }
  };

  if (!demoService.isDemoMode() || dismissed) {
    return null;
  }

  const features = demoInfo?.features_to_try || [];
  const nextFeature = features.find(f => !f.completed);
  const allCompleted = progress.completed === progress.total;
  const NextIcon = nextFeature ? FEATURE_CONFIG[nextFeature.id]?.icon : CelebrationIcon;

  return (
    <>
      {/* Floating suggestion chip */}
      <Fade in={!dismissed}>
        <Box
          sx={{
            position: 'fixed',
            bottom: { xs: 130, sm: 24 },
            left: { xs: 8, sm: 'auto' },
            right: { xs: 'auto', sm: 24 },
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}
        >
          {/* Main suggestion chip */}
          <Chip
            icon={NextIcon ? <NextIcon sx={{ fontSize: 18 }} /> : undefined}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {allCompleted 
                    ? '🎉 All done!' 
                    : `Try: ${FEATURE_CONFIG[nextFeature?.id]?.label || 'Next feature'}`}
                </Typography>
                <ArrowIcon sx={{ fontSize: 14, opacity: 0.7, flexShrink: 0 }} />
              </Box>
            }
            onClick={() => nextFeature && handleFeatureClick(nextFeature.id)}
            sx={{
              height: 36,
              bgcolor: 'primary.main',
              color: 'white',
              cursor: 'pointer',
              boxShadow: 2,
              maxWidth: { xs: 'calc(100vw - 140px)', sm: 'none' },
              '&:hover': {
                bgcolor: 'primary.dark',
                boxShadow: 4
              },
              '& .MuiChip-icon': {
                color: 'white'
              }
            }}
          />

          {/* More options button */}
          <IconButton
            size="small"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{
              bgcolor: 'background.paper',
              boxShadow: 2,
              width: 32,
              height: 32,
              '&:hover': { bgcolor: 'grey.100' }
            }}
          >
            <MoreIcon fontSize="small" />
          </IconButton>

          {/* Dismiss button */}
          <IconButton
            size="small"
            onClick={() => setDismissed(true)}
            sx={{
              bgcolor: 'background.paper',
              boxShadow: 1,
              width: 28,
              height: 28,
              '&:hover': { bgcolor: 'grey.100' }
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Fade>

      {/* Dropdown menu with all options */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 280,
            maxWidth: 'calc(100vw - 32px)',
            mt: -1
          }
        }}
      >
        {/* Progress header */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Demo Progress ({progress.completed}/{progress.total})
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={progress.percentage} 
            sx={{ height: 6, borderRadius: 3 }}
          />
          <Typography variant="caption" color="text.secondary">
            {demoInfo?.current_items || 0}/{demoInfo?.item_limit || 20} items used
          </Typography>
        </Box>

        <Divider />

        {/* Feature list */}
        {features.map((feature) => {
          const config = FEATURE_CONFIG[feature.id];
          const IconComp = config?.icon || BoxIcon;
          return (
            <MenuItem
              key={feature.id}
              onClick={() => handleFeatureClick(feature.id)}
              sx={{ 
                opacity: feature.completed ? 0.6 : 1,
                py: 1
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {feature.completed ? (
                  <CheckIcon color="success" fontSize="small" />
                ) : (
                  <IconComp color="primary" fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText 
                primary={config?.label || feature.title}
                primaryTypographyProps={{
                  variant: 'body2',
                  sx: { textDecoration: feature.completed ? 'line-through' : 'none' }
                }}
              />
            </MenuItem>
          );
        })}

        <Divider />

        {/* Actions */}
        <Box sx={{ p: 1.5, display: 'flex', gap: 1, flexDirection: 'column' }}>
          <Button
            variant="contained"
            color="success"
            size="small"
            fullWidth
            startIcon={<SignUpIcon />}
            href="/app/register"
            sx={{ textTransform: 'none' }}
          >
            Sign Up to Keep Your Data
          </Button>
          <Button
            variant="text"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleReset}
            disabled={resetting}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            {resetting ? 'Resetting...' : 'Reset Demo'}
          </Button>
        </Box>
      </Menu>
    </>
  );
};

export default DemoGuide;
